import { matchSorter } from 'match-sorter';
import { Classroom } from '@teetsh/app/src/types/classroom';

export interface ClassroomMatchInfo {
  idBe?: string;
  name?: string;
  degree?: string;
  match?: Classroom;
  rows?: object[];
}

function getBestMatchingForField(
  classroomsFromFile: ClassroomMatchInfo[],
  classrooms: Classroom[],
  field: 'idBe' | 'name',
  strict: boolean = false,
): [ClassroomMatchInfo[], Classroom[]] {
  let remainingClassrooms = classrooms;
  const firstRoundClassrooms = classroomsFromFile.map(cl => {
    if (cl[field]) {
      let matches = matchSorter(remainingClassrooms, cl[field] as string, {
        keys: [field],
        threshold: strict
          ? matchSorter.rankings.EQUAL
          : matchSorter.rankings.MATCHES,
      });

      // If we have multiple matches and a degree, prefer the one with matching degree
      if (matches.length > 1 && cl.degree) {
        const degreeMatch = matches.find(m => m.degree === cl.degree);
        if (degreeMatch) {
          matches = [degreeMatch];
        }
      }

      // Only assign match if degree is compatible or if no degree info is available
      const match = matches[0];
      if (match) {
        // If both classroom from file and existing classroom have degree info,
        // they must match to be assigned
        if (cl.degree && match.degree && cl.degree !== match.degree) {
          // Skip this match due to degree mismatch
          return cl;
        }

        remainingClassrooms = remainingClassrooms.filter(
          c => c.id !== match.id,
        );
        return { ...cl, match };
      }
    }
    return cl;
  });
  return [firstRoundClassrooms, remainingClassrooms];
}

export function matchClassrooms(
  classroomsFromFile: ClassroomMatchInfo[],
  classrooms: Classroom[],
): ClassroomMatchInfo[] {
  const [firstRoundClassrooms, firstRoundRemainingClassrooms] =
    getBestMatchingForField(classroomsFromFile, classrooms, 'idBe');
  // eslint-disable-next-line prefer-const
  let [secondRoundClassrooms, remainingClassrooms] = getBestMatchingForField(
    firstRoundClassrooms,
    firstRoundRemainingClassrooms,
    'name',
    true,
  );
  // Assign remaining classrooms to unmatched groups, but only if degrees are compatible
  return secondRoundClassrooms.map(cl => {
    if (cl.match || remainingClassrooms.length === 0) {
      return cl;
    }

    // Find a remaining classroom with compatible degree : if both have degree info, they must match
    const compatibleClassroom = remainingClassrooms.find(
      classroom => !!cl.degree && cl.degree === classroom.degree,
    );

    if (compatibleClassroom) {
      remainingClassrooms = remainingClassrooms.filter(
        c => c.id !== compatibleClassroom.id,
      );
      return { ...cl, match: compatibleClassroom };
    }

    // No compatible classroom found, leave unmatched
    return cl;
  });
}
